import { useMemo, useState } from 'react';

import {
  Anchor,
  Box,
  Icon,
  IconButton,
  Menu,
  PropertyCard,
  Table,
  Tag,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import {
  IconChevronDownFilled,
  IconChevronRightFilled,
  IconCircleFilled,
  IconMoreFilled,
} from '@zillow/constellation-icons';

import { LoadingImage, LoadingText } from '@zg-rentals/loading-components';
import { AccountViewListingGroupType, AccountViewListingStatus } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerAccountViewZrmV2WebApiHandler_options,
  useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import {
  emitMultiplexCollapseUnitsEvent,
  emitMultiplexExpandUnitsEvent,
  EVENT_IDS as MULTIPLEX_EVENT_IDS,
} from '@zg-rentals/zillow-rental-manager/analytics/events/multiplex';
import { formatAddress, getPhotoUrls } from '@zg-rentals/zillow-rental-manager/utils';

import {
  useGetPropertiesInquiries,
  useGetPropertiesPayment,
  useGetPropertyLeases,
} from '../../../../hooks/queries/properties/zrm';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import { cachePropertyAddress } from '../../../../utils/navigationCacheUtils';
import stringHelper from '../../../../utils/stringHelper';

import PropertyConstants from '../../../../constants/PropertyConstants';
import { ROUTES } from '../../../../constants/urls';

import EnableApplicationsModal from '../../../modals/EnableApplicationsModal';

import { minWidthZero, singleTextLineStyles } from '../../../../styles/text';
import {
  CELL_DIMENSIONS,
  cellAddressContainer,
  cellAnchorStyles,
  chevronContainerStyles,
  flexContainerStyles,
  groupUnitChildRowCellBackgroundCss,
  groupUnitChildRowTableCellPaddingCss,
  HiddenMobileTableCell,
  leftDataCellStyles,
  propertyPhotoContainerStyles,
  rightDataCellStyles,
  TableCell,
  tagStyles,
} from '../styles';

import {
  emitViewGeneratedLeaseEvent,
  EVENT_IDS as MANAGE_TENANTS_EVENT_IDS,
} from '../../../../analytics/events/manageTenants';
import {
  emitPropertyCardClickEvent,
  emitPropertyCardLeasesEvent,
  emitPropertyCardOverflowMenuEvent,
  emitPropertyCardPaymentsEvent,
  emitPropertyCardPotentialTenantsEvent,
  EVENT_IDS as PROPERTIES_PAGE_EVENT_IDS,
} from '../../../../analytics/events/propertiesPage';
import { useAuth } from '../../../AuthProvider';
import { TruncatedTextWithTooltip } from '../../../TruncatedTextWithTooltip';

import type React from 'react';
import type { TagPropsInterface } from '@zillow/constellation-10';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type {
  AccountViewListingGroupDto,
  AccountViewTableCellCtaDto,
  AccountViewTableCellDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type PropertyRowProps = {
  index: number;
  listing: AccountViewListingGroupDto;
  filters: { searchText: string; subNavFilterType: string };
  isLoading?: boolean;
  onUpsellCellHover: (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => void;
  isScrolledToLeft?: boolean;
  onExpandGroupClick?: () => void;
  isRowOpen?: boolean;
  isFetching?: boolean;
};

const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP, PROPERTY_TYPES } = PropertyConstants;

const IMAGE_SIZE = {
  PARENT_THUMB_HEIGHT: '72px',
  PARENT_THUMB_WIDTH: '92px',
  CHILD_THUMB_HEIGHT: '56px',
  CHILD_THUMB_WIDTH: '92px',
};

const PropertyRow = ({
  index,
  listing,
  isLoading: isComponentLoading = false,
  isScrolledToLeft = false,
  onExpandGroupClick,
  onUpsellCellHover,
  isRowOpen = false,
  isFetching = false,
  filters,
}: PropertyRowProps) => {
  const { user } = useAuth();
  const { isScreenXlGTE } = useScreenSize();

  const { enqueueToast } = useToast();

  const [displayApplicationsModal, setDisplayApplicationsModal] = useState(false);

  const cellWidth = CELL_DIMENSIONS.WIDTH;

  const textMaxWidth = {
    base: `${cellWidth + 100}px`,
    lg: `${cellWidth}px`,
    xxl: `${cellWidth + 50}px`,
  };

  // Extract fields from ListingGroup based on type. Multiplex groups have no listingAlias (use groupId for API calls).
  const listingAlias = listing.type === AccountViewListingGroupType.MULTIPLEX ? '' : listing.listingAlias;
  const name = listing.name;
  const listingStatusText = listing.status?.text || '';
  const listingStatus = listing.status?.status || '';

  const isRunningInProd =
    typeof window !== 'undefined'
      ? window.location.hostname.includes('zillow.com') || window.location.hostname.includes('hotpads.com')
      : false;

  const labelForPropertyTypesKey = (key: string | undefined) => {
    const k = key?.trim();
    if (k == null || k === '' || !Object.hasOwn(PROPERTY_TYPES, k)) {
      return undefined;
    }
    return PROPERTY_TYPES[k as keyof typeof PROPERTY_TYPES];
  };

  const isMultifamily = listing.isMultifamily || false;
  const groupId = listing.groupId;
  const address = listing.address;

  // Get photo URL from photoKey
  const thumb = listing.photoKey ? getPhotoUrls(isRunningInProd, listing.photoKey).thumb : '';

  const { listingCount = { total: 0, active: 0 } } = listing;

  /**
   * MULTIPLEX rows: use account-view `type` (maps to PROPERTY_TYPES).
   * LISTING rows: use `propertyType` from API.
   */
  const propertyTypeLabel =
    listing.type === AccountViewListingGroupType.MULTIPLEX
      ? labelForPropertyTypesKey(listing.type)
      : listing.type === AccountViewListingGroupType.LISTING
        ? labelForPropertyTypesKey(listing.propertyType)
        : undefined;

  const fullAddress = address.unit ? `${address.street} #${address.unit}` : address.street || name;
  const formattedCityStateZip = formatAddress(address as unknown as Record<string, string>).formattedCityStateZip;

  const shouldHideColumnContent =
    listingStatus === AccountViewListingStatus.ARCHIVED || listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH;

  const isParentPropertyRow = listing.type === AccountViewListingGroupType.MULTIPLEX;

  // row is a nested-unit-only
  const isGroupUnitChildRow =
    listing.groupId !== '' && listing.groupId !== listing.listingAlias && !isParentPropertyRow;

  const { data: paymentsData, isPending: isPaymentsPending } = useGetPropertiesPayment(
    {
      groupId,
      listingAlias,
      ...filters,
    },
    { enabled: !shouldHideColumnContent && !isComponentLoading },
  );

  const {
    data: inquiriesData,
    isPending: isInquiriesPending,
    refetch: refetchInquiries,
  } = useGetPropertiesInquiries(
    {
      groupId,
      listingAlias,
      ...filters,
    },
    { enabled: !shouldHideColumnContent && !isComponentLoading },
  );
  const { data: leasesData, isPending: isLeasesPending } = useGetPropertyLeases(
    {
      groupId,
      listingAlias,
      ...filters,
    },
    { enabled: !shouldHideColumnContent && !isComponentLoading },
  );
  const { data: optionsData, isPending: isMenuOptionsPending } = useRentalManagerAccountViewZrmV2WebApiHandler_options(
    { listingAlias },
    { enabled: Boolean(listingAlias) && !isComponentLoading },
  );

  const { data: { lease, ...webAnalyticsData } = {} } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: true,
    },
    { enabled: Boolean(listingAlias) && !isComponentLoading },
  );

  const { mutateAsync: setIsApplicationsAccepted } =
    useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted',
        },
        invalidates: ['users-properties-webAnalytics-get'],
      },
    });

  const targetListingRoute = useMemo(() => {
    if (isParentPropertyRow || !listingAlias) {
      return undefined;
    }

    if (isMultifamily) {
      return `/rental-manager${ROUTES.PROPERTIES}/${listingAlias}/listing`;
    }

    return `/rental-manager${ROUTES.PROPERTIES}/${listingAlias}/dashboard/overview`;
  }, [isMultifamily, listingAlias, isParentPropertyRow]);

  const routerHandler = (pathToRoute: string, openNewTab = false) => {
    if (openNewTab) {
      window.open(pathToRoute, '_blank');
    } else {
      window.location.href = pathToRoute;
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const openNewTab = event.ctrlKey || event.metaKey;
    event.preventDefault();
    event.stopPropagation();

    if (isParentPropertyRow && onExpandGroupClick) {
      trackMultiplexGroupToggleEvent();
      onExpandGroupClick();
    }

    if (!isParentPropertyRow) {
      emitPropertyCardClickEvent({ user, currentProperty: webAnalyticsData });
    }

    if (targetListingRoute) {
      cachePropertyAddress(listingAlias, address);
      routerHandler(targetListingRoute, openNewTab);
    }
  };

  const handleExpandGroupClick = (event?: React.MouseEvent<Element>) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!onExpandGroupClick) return;

    trackMultiplexGroupToggleEvent();
    onExpandGroupClick();
  };

  const trackMultiplexGroupToggleEvent = () => {
    if (isRowOpen) {
      emitMultiplexCollapseUnitsEvent({
        currentProperty: listing,
        user,
      });
    } else {
      emitMultiplexExpandUnitsEvent({
        currentProperty: listing,
        user,
      });
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    emitPropertyCardOverflowMenuEvent({
      user,
      currentProperty: webAnalyticsData,
      lease,
    });
  };

  const handleCellCtaClick = (
    event: React.MouseEvent<HTMLElement>,
    cta: Partial<AccountViewTableCellCtaDto>,
    label: string,
  ) => {
    event.stopPropagation();

    if (cta.text === 'Enable applications') {
      event.preventDefault();
      setDisplayApplicationsModal(true);
    }

    switch (label) {
      case PROPERTY_TABLE_COLUMNS.RENT:
        emitPropertyCardPaymentsEvent({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS:
        emitPropertyCardPotentialTenantsEvent({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.LEASES:
        emitPropertyCardLeasesEvent({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS
            ] || cta.text,
        });
        if (cta.text === 'View lease' && user) {
          emitViewGeneratedLeaseEvent({
            currentProperty: listing,
            user,
          });
        }
        break;
      default:
        break;
    }
  };

  const onEnableApplicationsClick = async () => {
    try {
      await setIsApplicationsAccepted({ postData: { propertyId: listingAlias, isApplicationsAccepted: true } });
      await refetchInquiries();
      enqueueToast(
        <Toast tone="success">
          Applications enabled for {fullAddress}. You can change preferences anytime on the Applications tab.
        </Toast>,
      );
    } catch {
      enqueueToast(<Toast tone="critical">Applications couldn’t be enabled. Try again.</Toast>);
    }
  };

  const getWrappedText = (value: string) => {
    let characterLimit = 30;
    characterLimit = isScreenXlGTE ? characterLimit : 23;
    const finalCharacterLimit = value.length > characterLimit ? characterLimit - 3 : characterLimit + 1;

    return stringHelper.wrapText(value, finalCharacterLimit);
  };

  const getPhotoContent = () => (
    <Box
      data-testid="PhotoContainer"
      css={{
        ...propertyPhotoContainerStyles,
        overflow: 'hidden',
        borderRadius: 'obj.soft',
        width: IMAGE_SIZE.PARENT_THUMB_WIDTH,
        height: isGroupUnitChildRow ? IMAGE_SIZE.CHILD_THUMB_HEIGHT : IMAGE_SIZE.PARENT_THUMB_HEIGHT,
      }}
    >
      <PropertyCard.Photo
        src={thumb}
        alt={fullAddress}
        fallbackImg={
          isComponentLoading ? (
            <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
          ) : (
            <PropertyCard.PhotoFallback />
          )
        }
        css={{ width: '100%', height: '100%' }}
      />
    </Box>
  );

  const getSingleSummaryStatusContent = () => {
    const singleSummaryStatusLine =
      propertyTypeLabel && listingStatusText
        ? `${propertyTypeLabel} • ${listingStatusText}`
        : (propertyTypeLabel ?? listingStatusText);

    const displayText = isGroupUnitChildRow === false ? singleSummaryStatusLine : listingStatusText;

    return (
      <Text textStyle="body-xs" asChild css={{ maxWidth: textMaxWidth }}>
        <p>
          <TruncatedTextWithTooltip
            text={displayText}
            textStyle="body-xs"
            color="text.subtle"
            css={singleTextLineStyles}
          />
        </p>
      </Text>
    );
  };

  const getGroupSummaryContent = () => {
    const displayText = `${propertyTypeLabel} • ${stringHelper.pluralize(listingCount.total, 'unit', 'units')}`;

    return (
      <Box css={{ ...minWidthZero }}>
        <Text textStyle="body-xs" asChild css={{ maxWidth: textMaxWidth }}>
          <p>
            <TruncatedTextWithTooltip
              text={displayText}
              textStyle="body-xs"
              color="text.subtle"
              css={singleTextLineStyles}
            />
          </p>
        </Text>
      </Box>
    );
  };

  const getCellText = ({ status, description, cta }: Partial<AccountViewTableCellDto>, label: string) => (
    <>
      {status &&
        (status.tagAppearance ? (
          <Tag
            size="sm"
            tone={status.tagAppearance === 'error' ? 'critical' : (status.tagAppearance as TagPropsInterface['tone'])}
            css={tagStyles}
          >
            {status.text}
          </Tag>
        ) : (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {status.url ? (
              <Anchor
                href={status.url}
                onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
                css={cellAnchorStyles}
              >
                {status.text}
              </Anchor>
            ) : (
              <Text textStyle="body-sm" asChild css={{ textWrap: 'balance' }}>
                <p>{status.text}</p>
              </Text>
            )}
            {status.hasUnread && (
              <Icon
                render={<IconCircleFilled />}
                size="sm"
                css={{ marginLeft: '200', color: 'icon.feedback.notify.hero', width: '200' }}
              />
            )}
          </Box>
        ))}
      {description && (
        <Text textStyle="body-sm" css={{ color: status ? 'text.subtle' : 'text.neutral' }} asChild>
          <p>{description}</p>
        </Text>
      )}
      {cta && (
        <TextButton
          asChild={!!cta.url}
          onClick={(event: React.MouseEvent<HTMLElement>) => handleCellCtaClick(event, cta, label)}
          textStyle="body-sm"
          data-eventid={cta.text === 'View lease' ? MANAGE_TENANTS_EVENT_IDS.VIEW_GENERATED_LEASE : undefined}
        >
          {cta.url ? <a href={cta.url || ''}>{cta.text}</a> : cta.text}
        </TextButton>
      )}
    </>
  );

  const getCellDataEventId = (label: string) => {
    if (label === PROPERTY_TABLE_COLUMNS.RENT) {
      return PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_PAYMENTS;
    }
    if (label === PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS) {
      return PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_POTENTIAL_TENANTS;
    }
    if (label === PROPERTY_TABLE_COLUMNS.LEASES) {
      return PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_LEASES;
    }
    return undefined;
  };

  const renderCellOrError = (
    data: Partial<AccountViewTableCellDto> | undefined | null,
    label: string,
    isCellPending: boolean,
  ) => {
    const shouldShowLoading = isComponentLoading || isCellPending;
    if (!data && !shouldShowLoading) {
      return <>-</>;
    }

    if (shouldHideColumnContent) {
      return <></>;
    }

    if (shouldShowLoading) {
      return (
        <LoadingText isLoading={true} textStyle="body-sm" loadingWidth="100%">
          -
        </LoadingText>
      );
    }

    return renderCellContent(data as Partial<AccountViewTableCellDto>, label);
  };

  const renderCellContent = (data: Partial<AccountViewTableCellDto>, label: string) => {
    const { status, description, cta, onlyDisplayOnHover } = data;

    if (!status && !description && !cta) {
      return <>-</>;
    }

    if (onlyDisplayOnHover) {
      return (
        <>
          <Box
            data-eventdescription={cta?.text}
            data-eventid={getCellDataEventId(label)}
            css={{ display: 'inline', 'td:hover &': { display: 'none' } }}
          >
            -
          </Box>
          <Box css={{ display: 'none', 'td:hover &': { display: 'block' } }}>
            {getCellText({ status, description, cta }, label)}
          </Box>
        </>
      );
    }

    return getCellText({ status, description, cta }, label);
  };

  const renderCell = (data: Partial<AccountViewTableCellDto> | undefined, label: string, isCellPending: boolean) => {
    return (
      <HiddenMobileTableCell
        id={label === PROPERTY_TABLE_COLUMNS.LEASES ? 'PropertyRow-Lease-Cell' : undefined}
        data-eventdescription="Property card hover"
        data-eventid={PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_HOVER}
        aria-labelledby={label}
        onMouseOver={() => {
          if (listingAlias && data?.onlyDisplayOnHover) {
            onUpsellCellHover(label, webAnalyticsData, lease);
          }
        }}
        css={
          isGroupUnitChildRow ? { ...groupUnitChildRowCellBackgroundCss, ...groupUnitChildRowTableCellPaddingCss } : {}
        }
      >
        {renderCellOrError(data, label, isCellPending)}
      </HiddenMobileTableCell>
    );
  };

  return (
    <>
      <Anchor
        href={targetListingRoute}
        onClick={handleRowClick}
        css={{
          display: 'contents',
          textDecoration: 'none',
        }}
      >
        {/* we need to keep onClick handler or this will always reload the page  */}
        <Table.Row
          id={index != null ? `PropertyRow-${index}` : undefined}
          interactive={true}
          onClick={handleRowClick}
          data-index={index.toString()}
          data-testid="PropertyRow"
          data-eventid={PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_CLICK}
          data-eventdescription="Property card click"
          css={{
            verticalAlign: 'middle',
            '& td': {
              cursor: 'pointer',
            },
          }}
        >
          <TableCell
            data-testid="PropertyRow-CellAddress"
            aria-labelledby={PROPERTY_TABLE_COLUMNS.ADDRESS}
            css={{
              ...leftDataCellStyles(isScrolledToLeft),
              ...(isGroupUnitChildRow ? groupUnitChildRowCellBackgroundCss : {}),
              ...(isGroupUnitChildRow ? groupUnitChildRowTableCellPaddingCss : {}),
            }}
          >
            <Box
              id={index != null ? `PropertyRow-${index}-Cell-0` : undefined}
              css={{
                ...cellAddressContainer,
              }}
              data-testid="PropertyRow-CellAddressContainer"
            >
              <Box data-testid="PropertyRow-ChevronContainer" css={chevronContainerStyles}>
                {isParentPropertyRow ? (
                  <IconButton
                    data-eventid={
                      isRowOpen
                        ? MULTIPLEX_EVENT_IDS.MULTIPLEX_COLLAPSE_UNITS
                        : MULTIPLEX_EVENT_IDS.MULTIPLEX_EXPAND_UNITS
                    }
                    title={isRowOpen ? 'Collapse group' : 'Expand group'}
                    aria-expanded={isRowOpen}
                    size="sm"
                    emphasis="bare"
                    icon={isRowOpen ? <IconChevronDownFilled /> : <IconChevronRightFilled />}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      !isFetching && handleExpandGroupClick(event)
                    }
                    disabled={isFetching}
                    css={{
                      color: 'icon.subtle',
                      opacity: isFetching ? 0.5 : 1,
                    }}
                  />
                ) : null}
              </Box>

              <Box
                data-testid="PropertyRow-PhotoAndAddressContainer"
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  flex: '1 1 0%',
                  alignItems: 'center',
                  minWidth: 0,
                  height: '100%',
                }}
              >
                {/* photo */}
                {getPhotoContent()}

                <Box css={{ ...flexContainerStyles, ...minWidthZero, flex: 1 }}>
                  <Box data-testid="AddressAndSummaryContainer" css={{ ...minWidthZero, flex: 1 }}>
                    <Box data-testid="Address">
                      <LoadingText
                        textStyle="body-sm"
                        asChild
                        isLoading={isComponentLoading}
                        loadingWidth="100%"
                        css={{ ...singleTextLineStyles, maxWidth: textMaxWidth }}
                      >
                        <p>{getWrappedText(name || fullAddress)}</p>
                      </LoadingText>
                    </Box>

                    {isGroupUnitChildRow === false && (
                      <Box data-testid="CityStateZip" css={{ marginBottom: '100' }}>
                        <LoadingText
                          textStyle="body-xs"
                          asChild
                          isLoading={isComponentLoading}
                          loadingWidth="50%"
                          css={{ ...singleTextLineStyles, maxWidth: textMaxWidth }}
                        >
                          <p>{getWrappedText(formattedCityStateZip)}</p>
                        </LoadingText>
                      </Box>
                    )}

                    <Box data-testid="Summary" css={{ ...minWidthZero }}>
                      {isParentPropertyRow ? (
                        <>
                          <Box data-testid="PropertyRow-Status">{getGroupSummaryContent()}</Box>
                        </>
                      ) : (
                        <>
                          <Box data-testid="PropertyRow-Status">{getSingleSummaryStatusContent()}</Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </TableCell>

          {renderCell(paymentsData as Partial<AccountViewTableCellDto>, PROPERTY_TABLE_COLUMNS.RENT, isPaymentsPending)}
          {renderCell(inquiriesData, PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS, isInquiriesPending)}
          {renderCell(leasesData, PROPERTY_TABLE_COLUMNS.LEASES, isLeasesPending)}
          <TableCell
            aria-labelledby={PROPERTY_TABLE_COLUMNS.ACTIONS}
            css={{
              ...rightDataCellStyles,
              ...(isGroupUnitChildRow ? groupUnitChildRowCellBackgroundCss : {}),
              ...(isGroupUnitChildRow ? groupUnitChildRowTableCellPaddingCss : {}),
            }}
          >
            <Box
              css={{
                display: 'flex',
                gap: '200',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!isParentPropertyRow && (
                <Menu
                  content={optionsData?.options?.map(
                    (item) =>
                      item && (
                        <Menu.Item key={item.type} asChild onClick={(event) => event.stopPropagation()}>
                          <a href={item.ctaUrl}>{item.ctaText}</a>
                        </Menu.Item>
                      ),
                  )}
                  trigger={
                    <IconButton
                      title={`Actions for ${name}`}
                      icon={<IconMoreFilled />}
                      emphasis="bare"
                      css={{ color: 'icon.accent.blue.hero', width: '1200' }}
                      onClick={handleMenuClick}
                      data-eventid={PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_OVERFLOW_MENU}
                      disabled={isMenuOptionsPending || isComponentLoading}
                    />
                  }
                />
              )}
            </Box>
          </TableCell>
        </Table.Row>
      </Anchor>
      {displayApplicationsModal && (
        <EnableApplicationsModal
          setShowEnableApplicationsModal={setDisplayApplicationsModal}
          onConfirm={onEnableApplicationsClick}
        />
      )}
    </>
  );
};

export { PropertyRow };
export type { PropertyRowProps };
export default PropertyRow;
